<template>
  <Dialog v-model:open="isOpen">
    <DialogTrigger as-child>
      <slot />
    </DialogTrigger>

    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{{ t("settings.inviteMember.title") }}</DialogTitle>
        <DialogDescription>
          {{ t("settings.inviteMember.description") }}
        </DialogDescription>
      </DialogHeader>

      <form class="space-y-4" @submit.prevent="onSubmit">
        <div class="space-y-2">
          <Label for="email">{{ t("settings.inviteMember.emailLabel") }}</Label>
          <Input
            id="email"
            v-model="formData.email"
            type="email"
            :placeholder="t('settings.inviteMember.emailPlaceholder')"
            :disabled="isLoading"
            required
          />
          <p class="text-sm text-muted-foreground">
            {{ t("settings.inviteMember.emailDescription") }}
          </p>
        </div>

        <DialogFooter class="gap-2">
          <Button
            type="button"
            variant="outline"
            :disabled="isLoading"
            @click="isOpen = false"
          >
            {{ t("settings.inviteMember.cancelButton") }}
          </Button>
          <Button type="submit" :disabled="isLoading || !formData.email.trim()">
            <LoadingIndicator v-if="isLoading" class="mr-2" />
            <UserPlus v-else class="w-4 h-4 mr-2" />
            {{ t("settings.inviteMember.inviteButton") }}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { reactive } from "vue";
import { inviteMember, useApiOrganizationMembers } from "~/composables/api";
import { successToast, errorToast } from "~/components/ui/toast";
import { UserPlus } from "lucide-vue-next";

const emit = defineEmits<{
  invited: [email: string];
}>();

const isOpen = ref(false);
const isLoading = ref(false);
const { t } = useI18n();
const { refresh } = await useApiOrganizationMembers();

const formData = reactive({
  email: ""
});

// Reset form when dialog closes
watch(isOpen, (newValue) => {
  if (!newValue) {
    formData.email = "";
  }
});

const onSubmit = async () => {
  if (!formData.email.trim()) return;

  isLoading.value = true;

  try {
    await inviteMember(formData.email.trim());

    // Show success toast
    successToast({
      title: t("settings.inviteMember.success.title"),
      description: t("settings.inviteMember.success.description", {
        email: formData.email.trim()
      })
    });

    // Refresh the members list
    await refresh();

    // Emit the invited event
    emit("invited", formData.email.trim());

    // Reset form and close dialog
    formData.email = "";
    isOpen.value = false;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error("Failed to invite member:", error);

    // Show error toast
    errorToast({
      title: t("settings.inviteMember.error.title"),
      description:
        error?.data?.statusMessage ||
        t("settings.inviteMember.error.description")
    });
  } finally {
    isLoading.value = false;
  }
};
</script>
